import { useEffect, useMemo, useState } from "react";

import useAxios from "@hooks/use-axios";
import { useNavigate } from "react-router-dom";
import { OPS_ENDPOINTS } from "@constants/endpoints";
import usePublisherKyc from "@panel/ops/publishers/hooks/use-publisher-kyc";
import { publisherDetailsPath, publisherDetailsState } from "@panel/ops/publishers/misc/constants";
import { canRemindKyc, isKycExpiringSoon } from "@panel/ops/publishers/misc/kyc";

const DEFAULT_LIMIT = 20;

const useBrandPublishers = (brandId, brandName) => {
    
    const { axiosInstance, loading } = useAxios({ base: "ops", initialLoader: true  });
    const [publishers, setPublishers] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT });
    const navigate = useNavigate(); 
    // Lets the publisher page breadcrumb back to this brand (on the Publishers tab),
    // and send KYC reminders under its name.
    const origin = { id: brandId, label: brandName, to: `/brands/details/${brandId}`, tab: "publishers" };

    const fetchPublishers = async (page = pagination.page, limit = pagination.limit) => {
        const res = await axiosInstance.get(OPS_ENDPOINTS.BRAND_PUBLISHERS(brandId), { params: { page, limit } });
        const result = res.data || res;
        const items = result.data || result.items || result.results || [];
        setPublishers(items);
        setPagination({
            total: result.total ?? items.length,
            page : result.page ?? page,
            limit: result.limit ?? limit
        });
    };

    const kyc = usePublisherKyc({ brand: { id: brandId, name: brandName }, onChanged: () => fetchPublishers() });

    useEffect(() => {
        fetchPublishers(1, DEFAULT_LIMIT);
    }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

    const expiringSoon = useMemo(() => publishers.filter(isKycExpiringSoon), [publishers]);
    const incomplete = useMemo(() => publishers.filter(canRemindKyc), [publishers]);

    const handlePageChange = (limit, page) => fetchPublishers(page, limit);
    const handlePerRowsChange = (limit) => fetchPublishers(1, limit);

    const payPublisher = async () => {
        navigate("/disbursements");
    };

    const openPublisher = (publisher) => navigate(publisherDetailsPath(publisher._id), { state: publisherDetailsState(publisher, origin) });

    return {
        publishers,
        pagination,
        loading,
        refetch: fetchPublishers,
        handlePageChange,
        handlePerRowsChange,
        payPublisher,
        openPublisher,
        origin,
        kyc,
        expiringSoon,
        incomplete
    };
};

export default useBrandPublishers;
